import React, { useContext, useState } from 'react';

import AJAX from 'common/AJAX';
import { ShowToastContext } from 'common/containers/ToastContainer';
import SourceFilter from 'common/subdomain/admin/AdminQueue/SourceFilters/SourceFilter';
import { useIntercomSourceFilter } from 'common/subdomain/admin/AdminQueue/SourceFilters/useIntercomSourceFilter';
import ButtonV2 from 'common/ui/ButtonV2';
import { H2, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import styles from 'css-module/components/subdomain/admin/AdminIntercomSettings/_TeamInboxes.module.scss';

import type { Company } from 'common/containers/CompanyContainer';

export default function TeamInboxes({ intercom }: { intercom: NonNullable<Company['intercom']> }) {
  const showToast = useContext(ShowToastContext);

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedInboxIDs, setSelectedInboxIDs] = useState<string[] | null>(
    intercom?.autopilotTeamInboxIDs ?? null,
  );
  const { loading, error, options, selectedOptions } = useIntercomSourceFilter(selectedInboxIDs);

  const handleChange = (teamInboxIDs: string[] | null) => {
    setHasChanges(true);
    setSelectedInboxIDs(teamInboxIDs);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(false);

    const response = await AJAX.post('/api/intercom/setTeamInboxes', {
      teamIDs: selectedInboxIDs,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    setSaving(false);

    if (error) {
      setSaveError(true);
    } else {
      showToast('Team inboxes saved.');
      setHasChanges(false);
    }
  };

  return (
    <div className={styles.adminIntercomSettingsTeamInboxes}>
      <H2 variant="headingMd">Autopilot settings</H2>
      <P className={styles.description} variant="bodyMd">
        Use filters to specify which conversations are processed by Autopilot.
      </P>
      <div className={styles.filterContainer}>
        <SourceFilter
          loading={loading}
          error={error}
          options={options}
          selectedOptions={selectedOptions}
          filterLabel="Filter by team inbox"
          onChange={handleChange}
          saveError={saveError}
          optionsPlacement="top"
        />
        <ButtonV2
          className={styles.saveButton}
          size="medium"
          disabled={!hasChanges}
          loading={saving}
          onClick={handleSave}>
          Save
        </ButtonV2>
      </div>
    </div>
  );
}
